import React, { useState, useEffect } from "react";
import { Box, Typography, Button, keyframes } from "@mui/material";
import { AutoAwesome, ArrowForward, PlayCircleOutline } from "@mui/icons-material";
import Navbar from "../../../NavBarRedesigned";
import MeetingModal from "../../../MeetingModal";
import geminiImage from "../assets/illustrations/Gemini_Generated_Image_sh8x5nsh8x5nsh8x_1.png";
import smartReportImage from "../assets/illustrations/Gemini_Generated_Image_ih3kuqih3kuqih3k (3).png";
import analyticsDashboardImage from "../assets/illustrations/Gemini_Generated_Image_shkp0sshkp0sshkp.png";
import healthToolsImage from "../assets/illustrations/Gemini_Generated_Image_4f8sys4f8sys4f8s.png";

// ============ ANIMATIONS ============
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// Accordion items data - Smart Reports, Analytics Dashboard, and Health Tools use new images, Viz App uses original
const accordionItems = [
  {
    id: 1,
    title: "Smart Reports",
    imageUrl: smartReportImage,
  },
  {
    id: 2,
    title: "Analytics Dashboard",
    imageUrl: analyticsDashboardImage,
  },
  {
    id: 3,
    title: "Viz App",
    imageUrl: geminiImage,
  },
  {
    id: 4,
    title: "Health Tools",
    imageUrl: healthToolsImage,
  },
];

// Accordion Item Component
const AccordionItem = ({ item, isActive, onMouseEnter, isMobile, index }) => {
  return (
    <Box
      onMouseEnter={onMouseEnter}
      onClick={onMouseEnter}
      sx={{
        position: "relative",
        height: isMobile ? "320px" : "490px",
        borderRadius: "16px",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.7s ease-in-out",
        width: isActive 
          ? (isMobile ? "180px" : "400px") 
          : (isMobile ? "45px" : "60px"),
        flexShrink: 0,
        animation: `${fadeIn} 0.6s ease-out ${0.5 + index * 0.1}s both`,
        backgroundColor: "#000",
      }}
    >
      {/* Background Image */}
      <Box
        component="img"
        src={item.imageUrl}
        alt={item.title}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://placehold.co/400x450/2d3748/ffffff?text=Image+Error";
        }}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          transition: "filter 0.7s ease-in-out, brightness 0.7s ease-in-out",
          filter: isActive ? "blur(0px) brightness(1.1)" : "blur(8px) brightness(0.7)",
          opacity: 1,
        }}
      />

      {/* No overlay when active - image should be bright and clear */}
      
      {/* Darker overlay when collapsed for better text visibility */}
      {!isActive && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            transition: "opacity 0.7s ease-in-out",
          }}
        />
      )}

      {/* Caption Text with 3D Shadow Effect */}
      <Typography
        sx={{
          position: "absolute",
          color: "#ffffff",
          fontSize: "18px",
          fontWeight: 700,
          whiteSpace: "nowrap",
          textShadow: `
            0 1px 0 rgba(0, 0, 0, 0.8),
            0 2px 0 rgba(0, 0, 0, 0.6),
            0 3px 0 rgba(0, 0, 0, 0.4),
            0 4px 0 rgba(0, 0, 0, 0.2),
            0 5px 10px rgba(0, 0, 0, 0.9),
            0 8px 20px rgba(0, 0, 0, 0.7),
            0 12px 30px rgba(0, 0, 0, 0.5)
          `,
          letterSpacing: "0.2px",
          transition: "all 0.3s ease-in-out",
          ...(isActive
            ? {
                bottom: "24px",
                left: "50%",
                transform: "translateX(-50%) rotate(0deg)",
              }
            : {
                bottom: "96px",
                left: "50%",
                transform: "translateX(-50%) rotate(90deg)",
              }),
        }}
      >
        {item.title}
      </Typography>
    </Box>
  );
};

const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleItemHover = (index) => {
    setActiveIndex(index);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "transparent",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Navbar - Integrated inside hero */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 20,
        }}
      >
        <Navbar transparent />
      </Box>

      {/* Main Section */}
      <Box
        sx={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: { xs: "130px 24px 60px", sm: "120px 32px 80px", md: "140px 48px 100px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: "36px", sm: "44px", md: "52px" },
          }}
        >
          {/* Left Side: Text Content */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            {/* AI-Powered Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: { xs: "6px 12px", sm: "7px 14px", md: "8px 16px" },
                marginBottom: { xs: "18px", md: "22px" },
                backgroundColor: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(53,103,214,0.15)",
                borderRadius: "9999px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                animation: `${fadeInUp} 0.8s ease-out`,
                "&:hover": {
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                },
              }}
            >
              <AutoAwesome sx={{ width: { xs: "14px", md: "16px" }, height: { xs: "14px", md: "16px" }, color: "#3567D6" }} />
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "12px", sm: "13px", md: "15px" },
                  fontWeight: 600,
                  color: "#1a1a1a",
                  whiteSpace: "nowrap",
                  letterSpacing: "0.2px",
                }}
              >
                AI-Powered Healthcare Revolution
              </Typography>
            </Box>

            {/* Main Heading */}
            <Typography
              component="h1"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "28px", sm: "38px", md: "46px", lg: "50px" },
                fontWeight: 700,
                color: "#111827",
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
                marginBottom: { xs: "18px", md: "22px" },
                animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
              }}
            >
              Transform Lab Reports into{" "}
              <Box
                component="span"
                sx={{
                  display: { xs: "inline", md: "block" },
                  background: "linear-gradient(135deg, #3567D6 0%, #10b981 50%, #8b5cf6 100%)",
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  animation: `${shimmer} 5s linear infinite`,
                }}
              >
              Insights & Business
              </Box>
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                fontWeight: 400,
                color: "#4B5563",
                lineHeight: 1.6,
                marginBottom: { xs: "22px", md: "26px" },
                maxWidth: "540px",
                mx: { xs: "auto", md: 0 },
                animation: `${fadeInUp} 0.8s ease-out 0.4s both`,
              }}
            >
              Unlock the full potential of your diagnostic data with AI-powered
              patient reports, automated engagement, and intelligent health
              analytics that patients actually understand.
            </Typography>

            {/* CTA Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "16px",
                justifyContent: { xs: "center", md: "flex-start" },
                alignItems: { xs: "center", md: "flex-start" },
                width: { xs: "100%", md: "auto" },
                animation: `${fadeInUp} 0.8s ease-out 0.6s both`,
              }}
            >
              <Button
                variant="contained"
                endIcon={<ArrowForward sx={{ fontSize: { xs: "18px", md: "20px" } }} />}
                onClick={() => setShowMeeting(true)}
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "14px", md: "15px" },
                  fontWeight: 600,
                  textTransform: "none",
                  padding: { xs: "10px 28px", md: "12px 32px" },
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #3567D6 0%, #5B8DEF 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 20px rgba(53, 103, 214, 0.3)",
                  transition: "all 0.3s ease",
                  width: { xs: "100%", sm: "auto" },
                  maxWidth: { xs: "260px", sm: "none" },
                  "&:hover": {
                    background: "linear-gradient(135deg, #2a57c4 0%, #4a7de0 100%)",
                    boxShadow: "0 6px 28px rgba(53, 103, 214, 0.4)",
                  },
                }}
              >
                Talk to Shweta
              </Button>
            </Box>
          </Box>

          {/* Right Side: Image Accordion */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              animation: `${fadeInRight} 1s ease-out 0.3s both`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: "8px", md: "16px" },
                padding: { xs: "8px", md: "16px" },
              }}
            >
              {accordionItems.map((item, index) => (
                <AccordionItem
                  key={item.id}
                  item={item}
                  isActive={index === activeIndex}
                  onMouseEnter={() => handleItemHover(index)}
                  isMobile={isMobile}
                  index={index}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* HubSpot Meeting Modal */}
      <MeetingModal showMeeting={showMeeting} setShowMeeting={setShowMeeting} />
    </Box>
  );
};

export default HeroSection;

